import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { TEMPLATE_VARIABLES, TemplateVariable } from '@/lib/templateVariables';

interface VariableFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
}

export function VariableField({
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 10,
  className,
}: VariableFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tokenStart, setTokenStart] = useState(-1);
  const [highlight, setHighlight] = useState(0);

  const filtered: TemplateVariable[] = open
    ? TEMPLATE_VARIABLES.filter((v) => {
        const q = query.toLowerCase();
        if (!q) return true;
        const inner = v.key.slice(2, -2).toLowerCase();
        return inner.includes(q) || v.label.toLowerCase().includes(q);
      })
    : [];

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered.length, highlight]);

  const updateAutocomplete = () => {
    const el = inputRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? value.length;
    const before = value.slice(0, pos);
    const match = before.match(/\{\{([^}\s]*)$/);
    if (match) {
      setOpen(true);
      setQuery(match[1]);
      setTokenStart(pos - match[1].length - 2);
    } else {
      setOpen(false);
      setTokenStart(-1);
    }
  };

  const insertVariable = (variable: string) => {
    const el = inputRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? value.length;
    const start = tokenStart >= 0 ? tokenStart : pos;
    const before = value.slice(0, start);
    const after = value.slice(pos);
    const next = before + variable + after;
    onChange(next);
    setOpen(false);
    setTokenStart(-1);
    setTimeout(() => {
      el.focus();
      const caret = (before + variable).length;
      el.setSelectionRange(caret, caret);
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertVariable(filtered[highlight].key);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const sharedProps = {
    value,
    placeholder,
    onChange: (e: any) => {
      onChange(e.target.value);
      requestAnimationFrame(updateAutocomplete);
    },
    onKeyDown: handleKeyDown,
    onKeyUp: updateAutocomplete,
    onClick: updateAutocomplete,
    onBlur: () => setTimeout(() => setOpen(false), 150),
    className: cn('input', multiline && 'min-h-[100px] resize-y', className),
  };

  return (
    <div className="relative">
      {multiline ? (
        <textarea
          ref={(el) => (inputRef.current = el)}
          rows={rows}
          {...sharedProps}
        />
      ) : (
        <input
          ref={(el) => (inputRef.current = el)}
          type="text"
          {...sharedProps}
        />
      )}

      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-grey-200 bg-white shadow-lg">
          {filtered.map((v, i) => (
            <button
              key={v.key}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertVariable(v.key);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm',
                i === highlight ? 'bg-grey-100' : 'hover:bg-grey-50',
              )}
            >
              <span className="font-medium text-secondary">{v.label}</span>
              <span className="font-mono text-xs text-grey-400">{v.key}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
